'use client';

import React, { useState } from 'react';

const blogPosts = [
  {
    title: "Hedge funds, Bitcoin, and digital currencies: as digital assets mature, hedge funds lean in",
    tag: "INSIGHTS AND ANALYSIS",
    imgBg: "#1a3a2a",
    imgContent: "bitcoin-chart",
  },
  {
    title: "Hyperliquid's Stablecoin Showdown: Five Giants Vie for a $5.5B DeFi Prize",
    tag: "INSIGHTS AND ANALYSIS",
    imgBg: "#0a2a2a",
    imgContent: "defi-coins",
  },
  {
    title: "Blockchain Analytics in 2025: What's Changed and What Hasn't",
    tag: "MARKET INTELLIGENCE",
    imgBg: "#1a1040",
    imgContent: "analytics",
  },
];

function BlogCardImage({ type, bg }) {
  if (type === "bitcoin-chart") {
    return (
      <svg viewBox="0 0 400 200" style={{ width: "100%", height: "100%", display: "block" }}>
        <rect width="400" height="200" fill={bg} />
        {[30,55,40,70,50,45,65,80,55,60,75,50,45,68,72,55,40,85,60,50].map((h, i) => (
          <rect key={i} x={i * 19 + 10} y={160 - h} width={12} height={h}
            fill={h > 60 ? "#22c55e" : "#ef4444"} opacity="0.85" rx="1" />
        ))}
        <circle cx="280" cy="110" r="55" fill="#f7931a" opacity="0.9" />
        <circle cx="280" cy="110" r="48" fill="#e67d00" />
        <text x="265" y="120" fontSize="38" fill="#fff" fontWeight="900" fontFamily="serif">₿</text>
        <ellipse cx="260" cy="158" rx="46" ry="10" fill="#f7931a" opacity="0.7" />
        <ellipse cx="260" cy="148" rx="46" ry="10" fill="#f7931a" opacity="0.8" />
        <circle cx="260" cy="130" r="24" fill="#f7931a" opacity="0.9" />
        <text x="248" y="138" fontSize="20" fill="#fff" fontWeight="900" fontFamily="serif">₿</text>
      </svg>
    );
  }
  if (type === "defi-coins") {
    return (
      <svg viewBox="0 0 400 200" style={{ width: "100%", height: "100%", display: "block" }}>
        <rect width="400" height="200" fill={bg} />
        {[0,1,2,3].map(i => (
          <line key={i} x1={i*100} y1="0" x2={i*100+200} y2="200" stroke="#0ff" strokeWidth="0.5" opacity="0.12" />
        ))}
        {[
          { x: 30, c: "#4a5568", label: "A" },
          { x: 100, c: "#718096", label: "⚙" },
          { x: 170, c: "#2d3748", label: "P" },
          { x: 240, c: "#22d3ee", label: "✦", highlight: true },
          { x: 310, c: "#a78bfa", label: "~", highlight2: true },
        ].map((coin, i) => (
          <g key={i}>
            <circle cx={coin.x + 30} cy="110" r="30"
              fill={coin.highlight ? "#22d3ee" : coin.highlight2 ? "#7c3aed" : coin.c} opacity="0.9" />
            <circle cx={coin.x + 30} cy="110" r="25"
              fill={coin.highlight ? "#06b6d4" : coin.highlight2 ? "#6d28d9" : "#2d3748"} opacity="0.95" />
            <text x={coin.x + 20} y="117" fontSize="16" fill="#fff" fontWeight="700">{coin.label}</text>
          </g>
        ))}
        <text x="10" y="30" fontSize="10" fill="#22d3ee" opacity="0.5" fontFamily="monospace">PAXOS</text>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 400 200" style={{ width: "100%", height: "100%", display: "block" }}>
      <rect width="400" height="200" fill={bg} />
      {[[80,60],[200,40],[320,80],[100,140],[240,160],[360,120]].map(([x,y], i, arr) => (
        <g key={i}>
          {arr.slice(i+1).map(([x2,y2], j) =>
            Math.hypot(x2-x,y2-y) < 180
              ? <line key={j} x1={x} y1={y} x2={x2} y2={y2} stroke="#a78bfa" strokeWidth="1" opacity="0.3" />
              : null
          )}
          <circle cx={x} cy={y} r="8" fill="#7c3aed" opacity="0.9" />
        </g>
      ))}
    </svg>
  );
}

function ArrowIcon({ dir }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: dir === "prev" ? "rotate(180deg)" : "none" }}>
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function BlogCard({ post, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        cursor: "pointer",
      }}
    >
      {/* Title */}
      <p style={{
        fontSize: "clamp(0.9rem, 1.4vw, 1rem)",
        fontWeight: 500,
        color: hovered ? "#a78bfa" : "#ffffff",
        lineHeight: 1.55,
        margin: "0 0 16px",
        transition: "color 0.2s",
      }}>
        {post.title}
      </p>

      {/* Image card */}
      <div style={{
        position: "relative",
        borderRadius: 14,
        overflow: "hidden",
        height: "clamp(160px, 22vw, 240px)",
        border: `1px solid ${hovered ? "#4c4880" : "#2d2a4a"}`,
        transition: "border-color 0.3s, transform 0.3s, box-shadow 0.3s",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hovered ? "0 16px 48px rgba(124,58,237,0.18)" : "none",
      }}>
        <div style={{ width: "100%", height: "100%" }}>
          <BlogCardImage type={post.imgContent} bg={post.imgBg} />
        </div>

        {/* Tag overlay */}
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background: "rgba(255,255,255,0.92)",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <span style={{
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: "#0f0f1a",
          }}>
            {post.tag}
          </span>
          {/* Small arrow in tag bar */}
          <div style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "#7c3aed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            transition: "transform 0.3s",
            transform: hovered ? "rotate(-40deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}>
            <ArrowIcon dir="next" />
          </div>
        </div>

        {/* Purple bottom bar */}
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "#7c3aed",
        }} />
      </div>
    </div>
  );
}

function NavButton({ dir, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "clamp(80px, 10vw, 100px)",
        height: 44,
        borderRadius: 999,
        border: `1.5px solid ${hovered ? "#7c3aed" : "#4c4880"}`,
        background: hovered ? "#1e1c35" : "transparent",
        color: "#ffffff",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        transition: "border-color 0.2s, background 0.2s, transform 0.2s",
        transform: hovered ? "scale(1.04)" : "scale(1)",
        fontSize: "0.85rem",
        fontWeight: 500,
        fontFamily: "inherit",
      }}
    >
      <ArrowIcon dir={dir} />
    </button>
  );
}

function BlogSection() {
  const [current, setCurrent] = useState(0);
  const visible = [
    blogPosts[current % blogPosts.length],
    blogPosts[(current + 1) % blogPosts.length],
  ];

  return (
    <section style={{
      background: "#13121f",
      padding: "clamp(40px, 7vw, 72px) clamp(16px, 5vw, 64px) clamp(48px, 7vw, 80px)",
      boxSizing: "border-box",
      
    }}>
      <div style={{
        maxWidth: 1400,
        margin: "0 auto",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
        gap: "clamp(32px, 4vw, 40px)",
        alignItems: "start",
      }}>

        {/* Left: Blog title + nav arrows */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: "clamp(24px, 4vw, 48px)",
          paddingTop: "clamp(0px, 4vw, 40px)",
        }}>
          <div>
            <h2 style={{
              fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
              fontWeight: 600,
              color: "#ffffff",
              margin: "0 0 16px",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}>
              The<br />elementuslabs<br />Blog
            </h2>
            <p style={{
              color: "#9ca3af",
              fontSize: "clamp(0.85rem, 1.3vw, 0.95rem)",
              lineHeight: 1.65,
              margin: 0,
            }}>
              Our take on what matters<br />in blockchain and crypto.
            </p>
          </div>

          {/* Nav arrows — pinned below on left column */}
          <div style={{ display: "flex", gap: 10 }}>
            <NavButton dir="prev" onClick={() => setCurrent(c => (c - 1 + blogPosts.length) % blogPosts.length)} />
            <NavButton dir="next" onClick={() => setCurrent(c => (c + 1) % blogPosts.length)} />
          </div>
        </div>

        {/* Blog cards */}
        {visible.map((post, i) => (
          <BlogCard key={`${current}-${i}`} post={post} index={i} />
        ))}
      </div>

      {/* Dot indicators */}
      <div style={{
        maxWidth: 1400,
        margin: "clamp(24px, 4vw, 40px) auto 0",
        display: "flex",
        justifyContent: "center",
        gap: 8,
      }}>
        {blogPosts.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            style={{
              width: i === current % blogPosts.length ? 24 : 8,
              height: 8,
              borderRadius: 999,
              background: i === current % blogPosts.length ? "#7c3aed" : "#4c4880",
              border: "none",
              cursor: "pointer",
              padding: 0,
              transition: "width 0.3s, background 0.3s",
            }}
          />
        ))}
      </div>
    </section>
  );
}

export default BlogSection;